import {Assignees} from '@github-ui/assignees'
import {ERRORS, HOTKEYS} from '@github-ui/item-picker'
import {
  AssigneeFragment,
  type AssigneePickerAssignee$data,
  type AssigneePickerAssignee$key,
  LazyAssigneeIssuePicker,
  commitUpdateIssueAssigneesMutation,
} from '@github-ui/item-picker/assignee'
import {
  LabelFragment,
  LazyLabelPicker,
  type LabelPickerLabel$key,
  type LabelPickerLabel$data,
} from '@github-ui/item-picker/label'
import {useToastContext} from '@github-ui/toast'
import {useMemo, Suspense, useCallback, useState} from 'react'
import {graphql, type PreloadedQuery, readInlineData, useRelayEnvironment} from 'react-relay'
import {useFragment, usePaginationFragment, usePreloadedQuery} from 'react-relay/hooks'

import {LABELS, TEST_IDS} from '../../constants'
import MetadataAssignedLabelsReaderFragment, {
  type MetadataSectionAssignedLabels$key,
} from './__generated__/MetadataSectionAssignedLabels.graphql'
import type {MetadataSectionAssignedLabelsQuery} from './__generated__/MetadataSectionAssignedLabelsQuery.graphql'
import type {MetadataSectionAssignees$key} from './__generated__/MetadataSectionAssignees.graphql'
import type {MetadataSectionFragment$key} from './__generated__/MetadataSectionFragment.graphql'
import type {MetadataSectionQuery} from './__generated__/MetadataSectionQuery.graphql'
import {LabelsList as Labels} from '@github-ui/labels-list'
import {ReadonlySectionHeader} from './ReadonlySectionHeader'
import {SectionHeader} from './SectionHeader'
import {Section} from './Section'
import {Link, SelectPanel} from '@primer/react'
import {noop} from '@github-ui/noop'
import {useKeyPress} from '@github-ui/use-key-press'

export const MetadataSectionGraphqlQuery = graphql`
  query MetadataSectionQuery($owner: String!, $repo: String!, $number: Int!) {
    repository(owner: $owner, name: $repo) {
      issue(number: $number) {
        ...MetadataSectionFragment
      }
    }
    viewer {
      # eslint-disable-next-line relay/must-colocate-fragment-spreads
      ...AssigneePickerAssignee
    }
  }
`

const metadataSectionAssignees = graphql`
  fragment MetadataSectionAssignees on Issue {
    assignees(first: 20) {
      edges {
        node {
          ...AssigneePickerAssignee
        }
      }
    }
  }
`

const metadataSectionFragment = graphql`
  fragment MetadataSectionFragment on Issue {
    id
    number
    repository {
      name
      owner {
        login
      }
    }
    ...MetadataSectionAssignees
    ...MetadataSectionAssignedLabels
    participants(first: 10) {
      edges {
        node {
          ...AssigneePickerAssignee
        }
      }
    }
    # eslint-disable-next-line relay/unused-fields
    viewerCanUpdateNext
    viewerCanAssign
    viewerCanLabel
  }
`

graphql`
  fragment MetadataSectionAssignedLabels on Labelable
  @argumentDefinitions(cursor: {type: "String"}, assignedLabelPageSize: {type: "Int", defaultValue: 20})
  @refetchable(queryName: "MetadataSectionAssignedLabelsQuery") {
    labels(first: $assignedLabelPageSize, after: $cursor) @connection(key: "MetadataSectionAssignedLabels_labels") {
      edges {
        node {
          # eslint-disable-next-line relay/must-colocate-fragment-spreads
          ...LabelPickerLabel
        }
      }
    }
  }
`

type MetadataSectionBaseProps = {
  onIssueUpdate?: () => void
  singleKeyShortcutsEnabled: boolean
  prefetchInteractionDataEnabled: boolean
}

type MetadataSectionProps = MetadataSectionBaseProps & {
  queryRef: PreloadedQuery<MetadataSectionQuery>
}

type MetadataSectionInternalProps = MetadataSectionBaseProps & {
  issue: MetadataSectionFragment$key
  viewer: AssigneePickerAssignee$key
}

type Assignee = AssigneePickerAssignee$data

export function MetadataSection({queryRef, ...rest}: MetadataSectionProps) {
  const preloadedData = usePreloadedQuery<MetadataSectionQuery>(MetadataSectionGraphqlQuery, queryRef)

  return preloadedData.repository?.issue ? (
    <MetadataSectionInternal issue={preloadedData.repository.issue} viewer={preloadedData.viewer} {...rest} />
  ) : null
}

export function MetadataSectionInternal({
  issue,
  viewer,
  onIssueUpdate,
  singleKeyShortcutsEnabled,
  prefetchInteractionDataEnabled,
}: MetadataSectionInternalProps) {
  const data = useFragment(metadataSectionFragment, issue)
  const {
    repository: {
      owner: {login: owner},
      name: repo,
    },
    number,
  } = data
  const assigneesData = useFragment<MetadataSectionAssignees$key>(metadataSectionAssignees, data)
  // eslint-disable-next-line no-restricted-syntax
  const viewerData = readInlineData(AssigneeFragment, viewer)

  const {data: labelDataRaw} = usePaginationFragment<
    MetadataSectionAssignedLabelsQuery,
    MetadataSectionAssignedLabels$key
  >(MetadataAssignedLabelsReaderFragment, data)

  const labelData = labelDataRaw

  const {id: issueId, viewerCanAssign, viewerCanLabel} = data

  const labels = useMemo(
    () =>
      labelData.labels?.edges?.flatMap(e =>
        // eslint-disable-next-line no-restricted-syntax
        e?.node ? [readInlineData<LabelPickerLabel$key>(LabelFragment, e?.node)] : [],
      ) || [],
    [labelData],
  )

  const participants = (data.participants?.edges || []).flatMap(participant =>
    participant && participant.node
      ? // eslint-disable-next-line no-restricted-syntax
        [readInlineData<AssigneePickerAssignee$key>(AssigneeFragment, participant.node)]
      : [],
  )

  const assignees = useMemo(() => {
    return (assigneesData.assignees?.edges || []).flatMap(assignee =>
      // eslint-disable-next-line no-restricted-syntax
      assignee && assignee.node ? [readInlineData<AssigneePickerAssignee$key>(AssigneeFragment, assignee.node)] : [],
    )
  }, [assigneesData])

  const {addToast} = useToastContext()
  const environment = useRelayEnvironment()

  const updateAssignees = useCallback(
    (selectedAssignees: Assignee[]) => {
      commitUpdateIssueAssigneesMutation({
        environment,
        input: {issueId, assignees: selectedAssignees, participants},
        onError: () =>
          addToast({
            type: 'error',
            message: ERRORS.couldNotUpdateAssignees,
          }),
        onCompleted: onIssueUpdate,
      })
    },
    [addToast, environment, issueId, onIssueUpdate, participants],
  )

  const assigneeSectionHeader = useMemo(() => {
    if (!viewerCanAssign) {
      return <ReadonlySectionHeader title={LABELS.sectionTitles.assignees} />
    }

    return prefetchInteractionDataEnabled ? (
      <Suspense fallback={<ReadonlySectionHeader title={LABELS.sectionTitles.assignees} />}>
        <LazyAssigneeIssuePicker
          itemIdentifier={{owner, repo, number}}
          readonly={!viewerCanAssign}
          shortcutEnabled={singleKeyShortcutsEnabled}
          assignees={assignees}
          viewer={viewerData}
          anchorElement={props => <SectionHeader title={LABELS.sectionTitles.assignees} buttonProps={props} />}
          onSelectionChange={(selectedAssignees: Assignee[]) => updateAssignees(selectedAssignees)}
        />
      </Suspense>
    ) : (
      <LazyAssigneeIssuePickerOnClick
        itemIdentifier={{owner, repo, number}}
        readonly={!viewerCanAssign}
        shortcutEnabled={singleKeyShortcutsEnabled}
        assignees={assignees}
        viewer={viewerData}
        onSelectionChange={(selectedAssignees: Assignee[]) => updateAssignees(selectedAssignees)}
      />
    )
  }, [
    viewerCanAssign,
    prefetchInteractionDataEnabled,
    owner,
    repo,
    number,
    singleKeyShortcutsEnabled,
    assignees,
    viewerData,
    updateAssignees,
  ])

  const labelSectionHeader = useMemo(() => {
    if (!viewerCanLabel) {
      return <ReadonlySectionHeader title={LABELS.sectionTitles.labels} />
    }

    return prefetchInteractionDataEnabled ? (
      <Suspense fallback={<ReadonlySectionHeader title={LABELS.sectionTitles.labels} />}>
        <LazyLabelPicker
          labelableId={issueId}
          repo={repo}
          owner={owner}
          labelableTypeName={'Issue'}
          readonly={!viewerCanLabel}
          shortcutEnabled={singleKeyShortcutsEnabled}
          labels={labels}
          onIssueUpdate={onIssueUpdate}
          anchorElement={props => <SectionHeader title={LABELS.sectionTitles.labels} buttonProps={props} />}
        />
      </Suspense>
    ) : (
      <LazyLabelPickerOnClick
        labelableId={issueId}
        repo={repo}
        owner={owner}
        labelableTypeName={'Issue'}
        readonly={!viewerCanLabel}
        shortcutEnabled={singleKeyShortcutsEnabled}
        labels={labels}
        onIssueUpdate={onIssueUpdate}
      />
    )
  }, [
    viewerCanLabel,
    prefetchInteractionDataEnabled,
    issueId,
    repo,
    owner,
    singleKeyShortcutsEnabled,
    labels,
    onIssueUpdate,
  ])

  const noAssigneeLabel = useMemo(() => {
    return viewerCanAssign ? (
      <>
        {LABELS.emptySections.assignees(true)}
        {
          <Link
            tabIndex={0}
            onClick={() => updateAssignees([viewerData])}
            sx={{color: 'fg.accent', fontWeight: 400, cursor: 'pointer', '&:hover': {textDecoration: 'none'}}}
          >
            {LABELS.emptySections.selfAssign}
          </Link>
        }
      </>
    ) : (
      LABELS.emptySections.assignees(false)
    )
  }, [viewerCanAssign, updateAssignees, viewerData])

  return (
    <>
      <Section sectionHeader={assigneeSectionHeader} emptyText={assignees.length > 0 ? undefined : noAssigneeLabel}>
        <Assignees assignees={assignees} />
      </Section>
      <Section
        sectionHeader={labelSectionHeader}
        emptyText={labels.length > 0 ? undefined : LABELS.emptySections.labels}
      >
        <Labels labels={labels} testId={TEST_IDS.issueLabels} />
      </Section>
    </>
  )
}

type LazyLabelPickerOnClickProps = {
  labelableId: string
  repo: string
  owner: string
  labelableTypeName: string
  readonly: boolean
  shortcutEnabled: boolean
  labels: LabelPickerLabel$data[]
  onIssueUpdate?: () => void
}

function LazyLabelPickerOnClick({
  labelableId,
  repo,
  owner,
  readonly,
  shortcutEnabled,
  labels,
  onIssueUpdate,
}: LazyLabelPickerOnClickProps) {
  const [wasTriggered, setWasTriggered] = useState(false)
  const [searchText, setSearchText] = useState('')

  useKeyPress(shortcutEnabled ? [HOTKEYS.labelPicker] : [], (e: KeyboardEvent) => {
    if (!wasTriggered) {
      setWasTriggered(true)
      e.preventDefault()
    }
  })

  if (!wasTriggered) {
    return <SectionHeader title={LABELS.sectionTitles.labels} buttonProps={{onClick: () => setWasTriggered(true)}} />
  }
  return (
    <Suspense
      fallback={
        <SelectPanel
          loading={true}
          placeholderText={'Filter labels'}
          open
          onOpenChange={noop}
          onSelectedChange={noop}
          items={[]}
          filterValue={searchText}
          onFilterChange={setSearchText}
          selected={undefined}
          renderAnchor={props => <SectionHeader title={LABELS.sectionTitles.labels} buttonProps={props} />}
          overlayProps={{width: 'medium', height: 'auto'}}
        />
      }
    >
      <LazyLabelPicker
        labelableId={labelableId}
        repo={repo}
        owner={owner}
        labelableTypeName={'Issue'}
        readonly={readonly}
        shortcutEnabled={shortcutEnabled}
        initialFilter={searchText}
        labels={labels}
        triggerOpen={true}
        onIssueUpdate={onIssueUpdate}
        anchorElement={props => <SectionHeader title={LABELS.sectionTitles.labels} buttonProps={props} />}
      />
    </Suspense>
  )
}

type LazyAssigneeIssuePickerOnClickProps = {
  itemIdentifier: {owner: string; repo: string; number: number}
  readonly: boolean
  shortcutEnabled: boolean
  assignees: AssigneePickerAssignee$data[]
  viewer: AssigneePickerAssignee$data
  onSelectionChange: (selectedAssignees: Assignee[]) => void
}

function LazyAssigneeIssuePickerOnClick({
  itemIdentifier,
  readonly,
  shortcutEnabled,
  assignees,
  viewer,
  onSelectionChange,
}: LazyAssigneeIssuePickerOnClickProps) {
  const [wasTriggered, setWasTriggered] = useState(false)
  const [searchText, setSearchText] = useState('')

  useKeyPress(shortcutEnabled ? [HOTKEYS.assigneePicker] : [], (e: KeyboardEvent) => {
    if (!wasTriggered) {
      setWasTriggered(true)
      e.preventDefault()
    }
  })

  if (!wasTriggered) {
    return <SectionHeader title={LABELS.sectionTitles.assignees} buttonProps={{onClick: () => setWasTriggered(true)}} />
  }

  return (
    <Suspense
      fallback={
        <SelectPanel
          loading={true}
          placeholderText={'Filter assignees'}
          open
          onOpenChange={() => {}}
          onSelectedChange={() => {}}
          items={[]}
          filterValue={searchText}
          onFilterChange={setSearchText}
          selected={[]}
          renderAnchor={props => <SectionHeader title={LABELS.sectionTitles.assignees} buttonProps={props} />}
          overlayProps={{width: 'medium', height: 'auto'}}
        />
      }
    >
      <LazyAssigneeIssuePicker
        itemIdentifier={itemIdentifier}
        readonly={readonly}
        shortcutEnabled={shortcutEnabled}
        assignees={assignees}
        triggerOpen={true}
        viewer={viewer}
        anchorElement={props => <SectionHeader title={LABELS.sectionTitles.assignees} buttonProps={props} />}
        onSelectionChange={onSelectionChange}
      />
    </Suspense>
  )
}

try{ MetadataSection.displayName ||= 'MetadataSection' } catch {}
try{ MetadataSectionInternal.displayName ||= 'MetadataSectionInternal' } catch {}
try{ LazyLabelPickerOnClick.displayName ||= 'LazyLabelPickerOnClick' } catch {}
try{ LazyAssigneeIssuePickerOnClick.displayName ||= 'LazyAssigneeIssuePickerOnClick' } catch {}